import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { FaClasses } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { ALIGN_CONTENT, JUSTIFY_CONTENT, MARGIN, PADDING, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { KeyboardInput, createHtmlElement, setHTMLElementContent, toggleAttribute } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { VALIDATION_SERVICE, ValidationService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { PropertyConfig, PropertyConfigValueMap } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { IsUniqueValidator } from "@cervval.js/common/src/services/validation.impl/validators/isUnique.validator";
import { RequiredValidator } from "@cervval.js/common/src/services/validation.impl/validators/required.validator";
import { validatorEntry } from "@cervval.js/common/src/services/validation.impl/validators/validator";
import { negativeBlackOrWhiteHex } from "@cervval.js/common/src/tools/color.tools";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { ValueOrFunction, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { format } from "@cervval.js/common/src/tools/timestamps.tools";
import { DEFAULT_DATE_FORMAT, toTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { ButtonOptions, ButtonSize, ButtonStyle, CjsButton } from "../../components/button/button";
import { IconFA6 } from "../../components/icon/tools/fontAwesome6IconList.tools";
import { PopupPosition, ShowPopupReturn, showPopup } from "../../components/showPopup/showPopup";
import { CjsSmartList } from "../../components/smartList/smartList";
import { DefaultEditorFactoryService } from "../../editors/defaultEditorFactory.service";
import { EDITOR_FACTORY_SERVICE } from "../../editors/editorFactory.service";
import { EditorWithPicker } from "../../editors/editorsWithPicker/editorWithPicker";
import { CjsEllipsis } from "../ellipsis/ellipsis";
import { getIconFaName } from "../icon/iconFa.tools";
import { I18nType, i18n } from "./i18n/i18n";

/** Value type returned by the default editor of a given propertyConfigType */
export type PropertyConfigValueType<T extends PropertyConfigType> = T extends keyof PropertyConfigValueMap ? PropertyConfigValueMap[T] : never;

/** PropertyConfigTypes usable as the addEditor of a customList */
export type CustomListPropertyConfigTypes = keyof PropertyConfigValueMap;

export interface CustomListOptions<P extends CustomListPropertyConfigTypes> {
    /** The propertyConfigType of the addEditor, it defines the type of the items (PropertyConfigValueType<P>) */
    addEditorPropertyConfigType: ValueOrFunction<P>;

    /** Callback invoked before a new item is added */
    onBeforeAdd?: (item: PropertyConfigValueType<P>) => PropertyConfigValueType<P>;

    /** Maximum number of rows displayed in the popup */
    maxRowDisplayed: number;

    /** Height of each row in pixels */
    rowHeight: number;

    /** Container is disabled */
    disabled: ValueOrFunction<boolean>;
}

/** Default property config type used by the custom list */
export const defaultCustomListPropertyConfigType = PropertyConfigType.STRING;

/** Default customList options */
export const defaultCustomListOptions: CustomListOptions<typeof defaultCustomListPropertyConfigType> = {
    addEditorPropertyConfigType: defaultCustomListPropertyConfigType,
    maxRowDisplayed: 6,
    rowHeight: 36,
    disabled: false
};

/** Options used for add and delete item buttons */
const commonButtonOptions: Partial<ButtonOptions> = {
    iconMode: "only",
    size: ButtonSize.SMALL
};

@CustomElement({
    name: "cjs-custom-list",
    template: require("./customList.html"),
    style: require("./customList.scss"),
    i18n,
    doNotRefreshAfterInit: true
})
export class CjsCustomList<P extends CustomListPropertyConfigTypes = typeof defaultCustomListPropertyConfigType> extends WebComponent<I18nType> {

    @Injected(EDITOR_FACTORY_SERVICE)
    public readonly editorFactoryService!: DefaultEditorFactoryService;

    @TypedWebComponentOptions(defaultCustomListOptions)
    public readonly options!: WebComponentOptions<CustomListOptions<P>>;

    @TypedHtmlElements()
    public readonly components!: {
        container: HTMLDivElement
    };

    /** Event emitted when a new item is added */
    @Event(NoBubblesEventInit)
    public onAddEvent!: EventHandler<PropertyConfigValueType<P>>;

    /** Event emitted when an item is deleted */
    @Event(NoBubblesEventInit)
    public onDeleteEvent!: EventHandler<PropertyConfigValueType<P>>;

    /** Event emitted when the popup is closed */
    @Event(NoBubblesEventInit)
    public onCloseEvent!: EventHandler;

    /** Holds list entries in a set */
    protected _itemsSet = new Set<PropertyConfigValueType<P>>();

    /** SmartList shown in the popup */
    public smartList: CjsSmartList = new CjsSmartList<PropertyConfigValueType<P>>();

    /** The popup's return value */
    protected _popup: ShowPopupReturn | undefined;

    /** The popup header div */
    public _headerDiv: HTMLDivElement | undefined;

    constructor(options?: Partial<CustomListOptions<P>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /** Get list values */
    public get itemsList(): PropertyConfigValueType<P>[] {
        return [...this._itemsSet];
    }

    /** get the value of disabled property of options  */
    public get disabled(): boolean {
        return Boolean(getVOFValue(this.options.disabled));
    }

    /** @inheritdoc */
    public override init(): void {
        this._initContainer();
        this._initSmartList();
    }

    /** @inheritdoc */
    private _initContainer(): void {
        this.components.container.addEventListener("mouseup", (e: Event) => {
            if (!this.disabled) {
                if (!this._popup) {
                    this.openPopup(e);
                } else {
                    this._popup.removePopup("close");
                }
            }
        });
    }

    /** Init smartList */
    private _initSmartList(): void {
        this.smartList.options.setWithoutRefresh({
            disableHeader: true,
            disableSort: true,
            rowHeight: this.options.rowHeight,
            border: {
                borderLess: true
            },
            selection: {
                enableBackgroundDisplay: false
            },
            renderCell: (cell) => this._renderRowElement(cell.rowItemData)
        });
    }

    /** @inheritdoc */
    public override refresh(): void {

        this._refreshDisabled();

        this._fillContainer();
    }

    /** 
     * Refresh the smartList and its container's height based on the current item list
     * When triggered from add operation, scroll to the added item
     */
    private _refreshSmartListPopup(options?: { fromAdd: boolean }): void {

        const triggerSelection = options?.fromAdd === true;

        this.smartList.setData({ data: this.itemsList, refresh: !triggerSelection }); // do not refresh if scroll is needed afterward (refresh is triggered on scroll)

        if (triggerSelection) {
            this.smartList.selectItems(this.itemsList.at(-1));
        }

        this._processElementsHeight();
    }

    /** Set data and refresh if option is not false */
    public setData(data: PropertyConfigValueType<P>[], options?: { refresh?: boolean }): void {
        this._itemsSet = new Set(data);

        if (options?.refresh !== false) {
            // Refresh components with new items
            this.refresh();
        }
    }

    /** Open the popup */
    public openPopup(handledClickEvent?: Event): void {
        if (this.disabled === true) {
            return;
        }

        this._headerDiv = this._renderCreationHeader();

        const popupContent = createHtmlElement("div", {
            innerHTML: [this._headerDiv, this.smartList],
            cssClass: ["d-flex", "flex-column"]
        });

        this._popup = showPopup({
            disablePopupInDialog: true,
            elementToShow: popupContent,
            popupPosition: PopupPosition.BOTTOM_START,
            parent: this,
            event: handledClickEvent,
            doNotCancelOnEscapeKey: true,
            doNotCloseOnEnterKey: true,
            onPopupHide: () => {
                this._popup = undefined;
                this.onCloseEvent.emit();
            },
            cssPopup: {
                style: {
                    width: toPx(this.components.container.getBoundingClientRect().width),
                    zIndex: "1"
                }
            }
        });

        this._inputAutoCompleteOffAndFocusIt();

        this._refreshSmartListPopup();
    }

    /** Disables autocomplete on the popup add input and focus it */
    private _inputAutoCompleteOffAndFocusIt(): void {
        const input = this._popup?.popup.querySelector<HTMLInputElement>("input");
        if (!input) { return; }
        input.autocomplete = "off";
        input.focus();
    }

    /** Renders the creation header editor based on propertyConfigType */
    private _renderCreationHeader(): HTMLDivElement {
        const addEditorType: PropertyConfigType | undefined = getVOFValue(this.options.addEditorPropertyConfigType);
        if (addEditorType === undefined) {
            throw new Error("addEditorPropertyConfigType is required");
        }

        const propertyName = "data";
        const propertyConfig: PropertyConfig = {
            label: "",
            propertyName,
            typeUid: addEditorType,
            validators: {
                ...validatorEntry(RequiredValidator, true),
                ...validatorEntry(IsUniqueValidator, { uniqueProperty: propertyName, getProperties: () => this.itemsList })
            }
        };

        const addItemEditor = this.editorFactoryService.newEditor(propertyConfig);

        // Save the original setValidationError function so we can call it later
        const originalSetValidationError = addItemEditor.setValidationError.bind(addItemEditor);

        // Override setValidationError to also recalculate popup heights when an error occurs
        addItemEditor.setValidationError = (...args: Parameters<typeof originalSetValidationError>) => {
            originalSetValidationError(...args);
            this._processElementsHeight();
        };

        // For an EditorWithPicker (in our case, a date editor), we need to apply this when the component is instantiated inside a dialog
        // This does not affect the normal case (when not in a dialog)
        if (addItemEditor instanceof EditorWithPicker) {
            addItemEditor.options.setWithoutRefresh({
                popupOptions: {
                    disablePopupInDialog: true,
                    parent: addItemEditor
                }
            });
        }

        // Shared callback to add a new value from editor input
        const handleAddValue = (): void => {
            const value = addItemEditor.getValue() as PropertyConfigValueType<P> | undefined;

            // Process addItemEditor validator
            const validation = injector.get<ValidationService>(VALIDATION_SERVICE);

            const propsValues: Record<string, PropertyConfigValueType<P> | undefined> = {
                [propertyName]: value
            };
            const validatedProps: Record<string, PropertyConfig> = {
                [propertyName]: propertyConfig
            };

            const errors = validation.validate(validatedProps, propsValues);
            addItemEditor.setValidationError(errors[propertyName]);

            const hasErrors = Object.keys(errors).length !== 0;

            if (value !== undefined && !hasErrors) {

                this._addItem(value);

                addItemEditor.setValue(undefined);
            }
        };

        const createBtn = new CjsButton({
            ...commonButtonOptions,
            cssStyle: ButtonStyle.PRIMARY,
            icon: {
                name: getIconFaName(IconFA6.PLUS),
                prefix: FaClasses.SOLID
            },
            tooltip: { title: this.i18n.addBtn.tooltip }
        });

        createBtn.clickEvent.on(() => {
            handleAddValue();
            addItemEditor.focus();
        });

        addItemEditor.addEventListener("keyup", (event) => {
            if (event.key === KeyboardInput.ENTER) {
                handleAddValue();
            }
        });

        addItemEditor.classList.add(W_100);
        const editorContainer = createHtmlElement("div", {
            innerHTML: [addItemEditor, createBtn],
            cssClass: ["d-flex", JUSTIFY_CONTENT.BETWEEN, "gap-2", ALIGN_CONTENT.CENTER, PADDING.P_2]
        });

        return editorContainer;
    }

    /** Handles adding a new item entry to the set */
    private _addItem(item: PropertyConfigValueType<P>): void {
        const sizeBefore = this._itemsSet.size;

        if (this.options.onBeforeAdd) {
            item = this.options.onBeforeAdd(item);
        }

        this._itemsSet.add(item);

        if (this._itemsSet.size > sizeBefore) {
            this.onAddEvent.emit(item);

            // Refresh smartList with new itemList content (and scroll to the added item)
            this._refreshSmartListPopup({ fromAdd: true });
            this._fillContainer();
        }
    }

    /** Handles removing a item entry from the set */
    private _deleteItem(itemToDelete: PropertyConfigValueType<P>): void {
        this._itemsSet.delete(itemToDelete);

        this.onDeleteEvent.emit(itemToDelete);

        // Refresh smartList with new itemList content
        this._refreshSmartListPopup();
        this._fillContainer();
    }

    /** Renders an HTML element representing a single row in the smartList */
    private _renderRowElement(item: PropertyConfigValueType<P>): HTMLElement {
        const deleteButton = new CjsButton({
            ...commonButtonOptions,
            cssStyle: ButtonStyle.DANGER,
            icon: {
                name: getIconFaName(IconFA6.XMARK),
                prefix: FaClasses.SOLID
            },
            tooltip: { title: this.i18n.deleteBtn.tooltip }
        });

        deleteButton.clickEvent.on(() => this._deleteItem(item));

        const ellipsis = new CjsEllipsis<string>(this._formatItem(item));

        const addEditorType: PropertyConfigType | undefined = getVOFValue(this.options.addEditorPropertyConfigType);
        if (addEditorType === undefined) {
            throw new Error("addEditorPropertyConfigType is required");
        }

        if (addEditorType === PropertyConfigType.COLOR) {
            const color = String(item);

            Object.assign(ellipsis.style, {
                backgroundColor: color,
                color: negativeBlackOrWhiteHex(color),
                paddingInline: "0.25rem",
                borderRadius: "0.25rem"
            });
        }

        const itemDiv = createHtmlElement("div", {
            innerHTML: [ellipsis, deleteButton],
            cssClass: ["d-flex", JUSTIFY_CONTENT.BETWEEN, W_100, MARGIN.MX_2]
        });

        return itemDiv;
    }

    /** Format an item list to a single string to display in the customList container */
    public formatItemsToSingleString(items: PropertyConfigValueType<P>[]): string {
        const formattedItems = items.map(item => this._formatItem(item));
        return formattedItems.join(" - ");
    }

    /** Formats item according to the addEditor propertyConfigType */
    private _formatItem(item: PropertyConfigValueType<P>): string {
        const addEditorType: PropertyConfigType | undefined = getVOFValue(this.options.addEditorPropertyConfigType);
        if (addEditorType === undefined) {
            throw new Error("addEditorPropertyConfigType is required");
        }

        if (addEditorType === PropertyConfigType.DATE) {
            return format(toTimestamp(Number(item)), DEFAULT_DATE_FORMAT);
        }

        return String(item);
    }

    /** Refresh disabled state */
    protected _refreshDisabled(): void {
        toggleAttribute(this, this.disabled, "disabled", "");
    }

    /** Updates popup and list container height based on rows and limits */
    private _processElementsHeight(): void {
        const headerHeight = this._headerDiv ? this._headerDiv.getBoundingClientRect().height : 0;
        const rowHeight = this.options.rowHeight;
        const nbRows = this._itemsSet.size;

        const totalHeight = headerHeight + rowHeight * nbRows;
        const maxHeight = headerHeight + rowHeight * this.options.maxRowDisplayed;

        if (this._popup) {
            this._popup.popup.style.height = toPx(totalHeight);
            this._popup.popup.style.maxHeight = toPx(maxHeight);
        }

        if (this.smartList.parentElement) {
            this.smartList.parentElement.style.height = toPx(totalHeight);
            this.smartList.parentElement.style.maxHeight = toPx(maxHeight);
        }
    }

    /** Fills the container content (empty or formatted items) */
    private _fillContainer(): void {
        const itemsList = this.itemsList;
        const content = itemsList.length > 0 ? this.formatItemsToSingleString(itemsList) : this.i18n.emptyContainer;

        setHTMLElementContent(this.components.container, content);
    }
}